/**
 * Simulation Service
 *
 * Handles all HTTP communication for the AI Simulation feature.
 * The simulation room itself connects directly to OpenAI Realtime
 * using the ephemeral_token returned by createSimulation / resume.
 */

import { apiClient } from "../lib/api/client";

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

export interface Persona {
    id: string;
    name: string;
    tone_label: string;
    tone: string;
}

export interface PersonasResponse {
    personas: Persona[];
}

export interface InterviewStage {
    id: string;
    label: string;
}

export interface StagesResponse {
    stages: InterviewStage[];
}

// ---------------------------------------------------------------------------
// Create Simulation
// ---------------------------------------------------------------------------

export interface CreateSimulationRequest {
    setup_context: {
        job_title: string;
        company?: {
            name: string;
            website?: string;
            country?: string;
            job_posting_url?: string;
        };
        job_description?: string;
        experience_level?: "ENTRY" | "MID" | "SENIOR";
        interview_stage?: string;
    };
    simulation_settings?: {
        persona?: string;           // e.g. "THE_ANALYST"
        difficulty?: "normal" | "challenging" | "intense";
        time_limit_seconds?: number; // 900 | 1800 | 2700 | 3600
        language?: string;           // "en-US" etc.
    };
}

export interface SimulationPersonaData {
    id: string;
    name: string;
    role: string;
    tone: string;
}

export interface CreateSimulationData {
    simulation_id: string;
    status: string;
    ephemeral_token: string;
    token_expires_at: number;
    strict_persona_prompt: string;
    time_limit_seconds: number;
    time_used_seconds: number;
    time_remaining_seconds: number;
    language: string;
    persona: SimulationPersonaData;
}

export interface CreateSimulationResponse {
    success: boolean;
    data: CreateSimulationData;
}

// ---------------------------------------------------------------------------
// Autosave
// ---------------------------------------------------------------------------

export interface TranscriptTurn {
    role: "assistant" | "user";
    content: string;
    timestamp_ms?: number;
}

export interface AutosaveRequest {
    transcript_delta?: TranscriptTurn[];
    time_used_seconds?: number;
}

export interface AutosaveResponse {
    success: boolean;
    data: {
        simulation_id: string;
        status: string;
        time_used_seconds: number;
        time_remaining_seconds: number;
        last_saved_at: string;
    };
}

// ---------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------

export interface ResumeRequest {
    client_state?: {
        last_known_time_used_seconds?: number;
    };
}

export interface ResumeActiveData {
    simulation_id: string;
    status: "active";
    ephemeral_token: string;
    token_expires_at: number;
    time_limit_seconds: number;
    time_used_seconds: number;
    time_remaining_seconds: number;
    strict_persona_prompt: string;
    language: string;
    persona: SimulationPersonaData;
    transcript_context: TranscriptTurn[];
}

export interface ResumeCompletedData {
    simulation_id: string;
    status: "completed";
    reason: string;
    analysis_status: string;
}

export type ResumeData = ResumeActiveData | ResumeCompletedData;

export interface ResumeResponse {
    success: boolean;
    data: ResumeData;
}

// ---------------------------------------------------------------------------
// Complete
// ---------------------------------------------------------------------------

export interface CompleteRequest {
    final_transcript?: TranscriptTurn[];
    time_used_seconds?: number;
}

export interface CompleteResponse {
    success: boolean;
    data: {
        simulation_id: string;
        status: "processing";
        analysis_status: string;
        message: string;
    };
}

// ---------------------------------------------------------------------------
// Get Simulation / Results
// ---------------------------------------------------------------------------

export interface SimulationReport {
    overview: {
        verdict: string;             // e.g. "Lean Yes"
        overall_score: number;
        confidence_level: string;
        summary: string;
    };
    score_breakdown: {
        communication: number;
        role_depth: number;
        problem_solving: number;
        behavioral_strength: number;
    };
    strengths: Array<{
        title: string;
        description: string;
        evidence_turns?: number[];
    }>;
    improvement_areas: Array<{
        area: string;
        why_it_matters: string;
        example_fix: string;
    }>;
    question_analysis: Array<{
        question_summary: string;
        performance_summary: string;
        scores: {
            technical_or_role_depth: number;
            communication: number;
            confidence: number;
        };
    }>;
    practice_focus: {
        priority: string[];
        action: string;
    };
}

export interface SimulationDetail {
    simulation_id: string;
    status: string;
    analysis_status: "not_started" | "queued" | "processing" | "completed" | "failed";
    job_title: string;
    company_name: string;
    interview_stage: string;
    years_of_experience: string;
    difficulty: string;
    language: string;
    persona: SimulationPersonaData;
    time_limit_seconds: number;
    time_used_seconds: number;
    created_at: string;
    completed_at?: string;
    transcript?: TranscriptTurn[];
    report: SimulationReport | null;
}

export interface GetSimulationResponse {
    success: boolean;
    data: SimulationDetail;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

// Cache to support pre-loading
let personasCache: PersonasResponse | null = null;
let stagesCache: StagesResponse | null = null;

export const simulationService = {
    /**
     * Fetch available interviewer personas.
     * GET /api/simulations/personas — No auth required.
     */
    async getPersonas(): Promise<PersonasResponse> {
        if (personasCache) return personasCache;
        const res = await apiClient.get<PersonasResponse>("/api/simulations/personas");
        personasCache = res;
        return res;
    },

    /**
     * Fetch standard interview stages.
     * GET /api/simulations/stages — No auth required.
     */
    async getStages(): Promise<StagesResponse> {
        if (stagesCache) return stagesCache;
        const res = await apiClient.get<StagesResponse>("/api/simulations/stages");
        stagesCache = res;
        return res;
    },

    /**
     * Create a new simulation session.
     * POST /api/create-simulation
     * Returns ephemeral_token for direct OpenAI Realtime connection.
     */
    async createSimulation(payload: CreateSimulationRequest): Promise<CreateSimulationData> {
        return await apiClient.post<CreateSimulationData>("/api/create-simulation", payload);
    },

    /**
     * Incrementally persist transcript turns and elapsed time.
     * POST /api/simulations/:id/autosave
     * Safe to call every 30s during the interview.
     */
    async autosave(id: string, payload: AutosaveRequest): Promise<AutosaveResponse["data"]> {
        return await apiClient.post<AutosaveResponse["data"]>(`/api/simulations/${id}/autosave`, payload);
    },

    /**
     * Smart resume endpoint — backend decides outcome automatically.
     * POST /api/simulations/:id/resume
     * Returns active (reconnect) or completed (show end screen).
     */
    async resume(id: string, payload?: ResumeRequest): Promise<ResumeData> {
        return await apiClient.post<ResumeData>(
            `/api/simulations/${id}/resume`,
            payload ?? {}
        );
    },

    /**
     * Finalize the interview. Kicks off async GPT-4o analysis.
     * POST /api/simulations/:id/complete
     * Navigate to results page and poll GET /:id after this.
     */
    async complete(id: string, payload?: CompleteRequest): Promise<CompleteResponse["data"]> {
        return await apiClient.post<CompleteResponse["data"]>(
            `/api/simulations/${id}/complete`,
            payload ?? {}
        );
    },

    /**
     * Get full simulation state and analysis report.
     * GET /api/simulations/:id
     * Poll every 3s after /complete until analysis_status = "completed".
     */
    async getSimulation(id: string): Promise<SimulationDetail> {
        return await apiClient.get<SimulationDetail>(`/api/simulations/${id}`);
    },
};
